import React, { useCallback, useState } from 'react'
import { Field, Form, Formik } from 'formik'
import * as Yup from 'yup'
import { useHistory, useParams } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { isEmpty } from 'lodash'
import { v4 as uuid } from 'uuid'

import Modal from 'components/Modal'
import TextBox from 'components/TextBox'
import AssignedItemsButton from 'components/AssignedItemsButton'
import ReportingCategoriesAssignedItems from 'components/ReportingCategories/ReportingCategoriesAssignedItems'

import { getCurrentCategory } from 'store/Categories/selectors'
import {
  getItems,
  getItemsAssignedToReportingCategory,
  getItemsWithCurrentReportingCategory,
  getItemsWithoutReportingCategory,
} from 'store/Items/selector'
import {
  createReportingCategory,
  updateReportingCategories,
} from 'store/Categories/actions'
import { transformItemsToUpdateInCategory } from 'utils/transformedItemsToCategory'
import { updateGlobalItem } from 'store/Items/actions'
import { getCurrentVenueUuid } from 'store/Venues/selector'

const ReportingCategoriesForm = () => {
  const [showValidationErrors, setShowValidationErrors] =
    useState(false)
  const [formStep, setFormStep] = useState(1)

  const history = useHistory()
  const dispatch = useDispatch()
  const { reportingCategoryId } = useParams()

  const isNewReportingCategories = reportingCategoryId === 'new'

  const currentVenueUuid = useSelector(getCurrentVenueUuid)
  const currentReportingCategory = useSelector((state) =>
    getCurrentCategory(state, reportingCategoryId)
  )

  const assignedItemsToReportingCategory = useSelector((state) =>
    getItemsAssignedToReportingCategory(state, reportingCategoryId)
  )

  const assignedItemsToReportingCategoryUuid =
    assignedItemsToReportingCategory.map((item) => item.uuid)

  const allItems = useSelector((state) => getItems(state))

  const itemsWithoutAnyReportingCategory = useSelector(
    getItemsWithoutReportingCategory
  )

  const itemsWithCurrentReportingCategory = useSelector((state) =>
    getItemsWithCurrentReportingCategory(state, reportingCategoryId)
  )

  const itemsWithCurrentReportingCategoryAndItemsWithoutAnyReportingCategory =
    [
      ...itemsWithCurrentReportingCategory,
      ...itemsWithoutAnyReportingCategory,
    ]

  const initialValues = {
    reportingCategoryName: currentReportingCategory?.name,
    items: assignedItemsToReportingCategoryUuid,
  }

  const validationSchema = Yup.object().shape({
    reportingCategoryName: Yup.string().required('Required'),
  })

  const disableUpdateButton = useCallback(
    (dirty, errors) => {
      const disableButton =
        !dirty || (showValidationErrors && !isEmpty(errors))

      return disableButton
    },
    [showValidationErrors]
  )

  const onClickSaveButton = useCallback(() => {
    setShowValidationErrors(true)
  }, [])

  const goBack = () => setFormStep(1)
  const onCloseModal = () => history.goBack()

  const modalTitle = isNewReportingCategories
    ? 'Add Reporting Category'
    : 'Edit Reporting Category'
  const modalStepTitle = formStep === 1 ? modalTitle : 'Items'

  const onSubmit = (values) => {
    const updatedItems = transformItemsToUpdateInCategory(
      initialValues?.items,
      allItems,
      values?.items,
      reportingCategoryId
    )
    const createReportingCategoryData = [
      {
        name: values.reportingCategoryName,
        canopyDisplayName: values.reportingCategoryName,
        majorCategory: true,
        uuid: uuid(),
        venueUuid: currentVenueUuid,
        productType: 'Food',
      },
    ]

    const editedReportingCategory = [
      {
        ...currentReportingCategory,
        name: values.reportingCategoryName,
        canopyDisplayName: values.reportingCategoryName,
        productType: 'Food',
      },
    ]
    if (isNewReportingCategories) {
      dispatch(createReportingCategory(createReportingCategoryData))
        .then((res) => {
          const newItemsParams = transformItemsToUpdateInCategory(
            initialValues?.items,
            allItems,
            values?.items,
            res?.payload[0].uuid
          )
          if (!isEmpty(newItemsParams)) {
            return dispatch(updateGlobalItem(newItemsParams))
          }
        })
        .then(() => history.push('/reporting-categories'))
    } else {
      dispatch(updateReportingCategories(editedReportingCategory))
        .then(() => {
          if (!isEmpty(updatedItems)) {
            return dispatch(updateGlobalItem(updatedItems))
          }
        })
        .then(() => history.push('/reporting-categories'))
    }
  }

  return (
    <Modal
      title={modalStepTitle}
      onClose={onCloseModal}
      className="-narrow"
      isReturnIcon={formStep === 2}
      onClickReturn={() => setFormStep(1)}
    >
      <div className="modal-body">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
        >
          {({ dirty, errors, setFieldValue, values }) => (
            <Form>
              {formStep === 1 && (
                <div className="item-settings">
                  <section className="item-settings__section">
                    <Field
                      component={TextBox}
                      label="Name"
                      name="reportingCategoryName"
                      showValidationErrors={showValidationErrors}
                    />
                  </section>
                  <h3 className="header-title">Items</h3>
                  <AssignedItemsButton
                    label="Select Items"
                    assignedItems={values?.items?.length}
                    handleClick={() => setFormStep(2)}
                  />

                  {/* <Autocomplete
                handleValueChange={(value) => {
                  setFieldValue('items', value)
                }}
                value={values.items}
                isMulti
                data={
                  itemsWithCurrentReportingCategoryAndItemsWithoutAnyReportingCategory
                }
                placeholder="Items"
                showValidationErrors={showValidationErrors}
              /> */}
                  <div className="btn-wrapper">
                    <button
                      className="btn -update"
                      onClick={onClickSaveButton}
                      disabled={disableUpdateButton(dirty, errors)}
                      type="submit"
                    >
                      Save
                    </button>
                  </div>
                </div>
              )}
              {formStep === 2 && (
                <ReportingCategoriesAssignedItems
                  goBack={goBack}
                  allItems={
                    itemsWithCurrentReportingCategoryAndItemsWithoutAnyReportingCategory
                  }
                />
              )}
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  )
}

export default ReportingCategoriesForm
